import type { Element, StatBlock } from '../core/types';
import { SeededRandom } from '../core/Random';

const advantage: Partial<Record<Element, Element>> = {
  Flame: 'Nature', Nature: 'Storm', Storm: 'Mechanical', Mechanical: 'Frost', Frost: 'Flame', Light: 'Shadow', Shadow: 'Light', Arcane: 'Neutral',
};

export interface DamageInput {
  attacker: StatBlock;
  defender: StatBlock;
  multiplier: number;
  attackerElement: Element;
  defenderElement: Element;
  damageBonus?: number;
  vulnerability?: number;
  forceCritical?: boolean;
}

export interface DamageResult { amount: number; critical: boolean; elemental: 'advantage' | 'disadvantage' | 'neutral' }

export class DamageCalculator {
  constructor(private readonly rng: SeededRandom) {}

  calculate(input: DamageInput): DamageResult {
    const attack = Math.max(1, input.attacker.attack);
    const defense = Math.max(0, input.defender.defense);
    const mitigation = 1000 / (1000 + defense * 4.2);
    const critical = input.forceCritical ?? this.rng.next() < Math.min(0.85, Math.max(0, input.attacker.critRate));
    const criticalMultiplier = critical ? Math.max(1, input.attacker.critDamage) : 1;
    const hasAdvantage = advantage[input.attackerElement] === input.defenderElement;
    const hasDisadvantage = advantage[input.defenderElement] === input.attackerElement;
    const elementalMultiplier = hasAdvantage ? 1.18 : hasDisadvantage ? 0.88 : 1;
    const elemental = hasAdvantage ? 'advantage' : hasDisadvantage ? 'disadvantage' : 'neutral';
    const bonus = 1 + Math.max(-0.8, (input.damageBonus ?? 0) + input.attacker.elementalDamage + (input.vulnerability ?? 0));
    const reduction = 1 - Math.min(0.75, Math.max(0, input.defender.damageReduction));
    const variance = 0.95 + this.rng.next() * 0.1;
    const amount = Math.max(1, Math.round(attack * Math.max(0, input.multiplier) * mitigation * criticalMultiplier * elementalMultiplier * bonus * reduction * variance));
    return { amount, critical, elemental };
  }

  heal(source: StatBlock, multiplier: number): number {
    return Math.max(0, Math.round(source.attack * Math.max(0, multiplier) * (1 + Math.max(0, source.healingBonus))));
  }
}
